import areaApi from '@api/areaApi';
import locationApi from '@api/locationApi';
import ImageInput from '@components/imageInput';
import ItemEditor from '@components/itemEditor';
import useAuth from '@hooks/useAuth';
import { t } from '@i18n';
import { InputWrapper, TextInput } from '@mantine/core';
import type { Area, AreaDraft } from '@models/area';
import { createFileRoute } from '@tanstack/react-router';
import { createForm } from 'cross-state/react';

export const Route = createFileRoute('/profile/locations/$locationId/areas/$id')({
  component: AreaEditor,
});

const defaultValue: Area = {
  id: '',
  name: '',
};

const areaForm = createForm<AreaDraft>({
  defaultValue,
  validations: {
    name: { required: (x) => !!x },
  },
});

function AreaEditor() {
  const { locationId } = Route.useParams();
  const [location] = locationApi.get(locationId).useCache();
  const { hasClaim } = useAuth();
  const api = areaApi.forLocation(locationId);

  return (
    <ItemEditor
      domain="areas"
      api={api}
      readonly={!hasClaim('locations/write', locationId)}
      back={{ to: `/profile/locations/${locationId}` }}
      form={areaForm}
      name={(area) => area.name}
      prepareDraft={async (draft) => {
        let image = draft.image;
        if (image instanceof File) {
          image = await api.uploadMap(image);
        }

        return { ...draft, image };
      }}
    >
      {() => (
        <>
          <InputWrapper label={t('areas.properties.location.label')}>
            <div>{location?.name ?? locationId}</div>
          </InputWrapper>

          <areaForm.Field
            name="name"
            render={(props) => (
              <TextInput
                {...props}
                label={t('areas.properties.name.label')}
                withAsterisk
                autoComplete="off"
                spellCheck={false}
              />
            )}
          />

          <areaForm.Field
            name="image"
            render={(props) => (
              <ImageInput
                {...props}
                label={t('areas.properties.map.label')}
                description={t('areas.properties.map.description')}
              />
            )}
          />
        </>
      )}
    </ItemEditor>
  );
}
